import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';

const QuickSearch = ({
  query,
  result,
  showResult,
  showFullForm,
  onSearch,
  onBlur,
  onKeyDown,
  toggleShowFullForm,
  selectedItem,
  searchUrl,
}) => {
  const searchInput = useRef(null);
  const quickSearchTrigger = useRef(null);
  const quickSearch = useRef(null);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleQuickSearchTriggerClick = () => {
    if (quickSearch.current) {
      quickSearch.current.classList.toggle('hidden');
      quickSearch.current.classList.toggle('flex');
    }
  };

  useEffect(() => {
    if (quickSearchTrigger.current) {
      quickSearchTrigger.current.addEventListener(
        'click',
        handleQuickSearchTriggerClick
      );
    }
    return () => {
      if (quickSearchTrigger.current) {
        quickSearchTrigger.current.removeEventListener(
          'click',
          handleQuickSearchTriggerClick
        );
      }
    };
  }, []);

  useEffect(() => {
    if (searchInput.current && !isTouchDevice) {
      searchInput.current.focus({ preventScroll: true });
    }
    if (showFullForm) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [showFullForm]);

  return (
    <>
      <div className="flex justify-end lg:hidden">
        <button
          type="button"
          className="flex"
          ref={quickSearchTrigger}
          aria-label="Trigger quick search"
        >
          <span className="icon icon-outlined" aria-hidden="true">
            search
          </span>
        </button>
      </div>
      <div
        className="absolute left-0 right-0 top-full hidden w-full bg-white p-4 lg:static lg:block lg:bg-transparent lg:p-0 max-h-[calc(100dvh-var(--mobile-header-height)-2rem)] lg:max-h-[calc(100dvh-var(--desktop-header-height)-2rem)]"
        ref={quickSearch}
      >
        <div className="quick-search relative flex flex-col w-full">
          <div className="bg-input-primary rounded-radius relative mx-auto flex h-12 w-full max-w-[27.625rem] items-center gap-2 overflow-hidden px-6 py-2 text-input-secondary shrink-0">
            <span className="icon icon-outlined" aria-hidden="true">
              search
            </span>
            <input
              className="w-full text-base leading-5 outline-none"
              type="text"
              placeholder={translate('general.search')}
              autoComplete="off"
              value={decodeURIComponent(query)}
              onChange={(event) => {
                onSearch(encodeURIComponent(event.target.value));
                if (!showFullForm) {
                  toggleShowFullForm();
                }
              }}
              onKeyDown={(event) => {
                if (showFullForm && event.key === 'Escape') {
                  onSearch('');
                  toggleShowFullForm();
                } else {
                  onKeyDown(event, {
                    searchUrl,
                  });
                }
              }}
              ref={searchInput}
              onBlur={() => onBlur()}
            />
            <div>
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    onSearch('');
                    toggleShowFullForm();
                  }}
                >
                  <span
                    className="icon icon-outlined !text-base"
                    aria-hidden="true"
                  >
                    close
                  </span>
                </button>
              )}
            </div>
          </div>
          {showFullForm && query && (
            <QuickSearchResult
              result={result}
              selectedItem={selectedItem}
              searchUrl={searchUrl}
              onClose={() => {
                onSearch('');
                toggleShowFullForm();
              }}
            />
          )}
        </div>
      </div>
      {showFullForm && (
        <div
          className="-z-1 fixed inset-[var(--mobile-header-height)_0_0_0] bg-white lg:inset-0 lg:bg-black/50"
          onClick={toggleShowFullForm}
        ></div>
      )}
    </>
  );
};

QuickSearch.propTypes = {
  query: PropTypes.string,
  result: PropTypes.array,
  showResult: PropTypes.bool,
  showFullForm: PropTypes.bool,
  selectedItem: PropTypes.number,
  onSearch: PropTypes.func,
  toggleShowFullForm: PropTypes.func,
  setSearchQuery: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  searchUrl: PropTypes.string,
};

export default QuickSearch;
